import React, { useContext, useEffect, useRef } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import { useToast } from 'js/design_system/toasts/hooks';
import { CiterightContext } from '../CiterightContextProvider';

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-12);
  padding: var(--spacing-xs-8) var(--spacing-sm-24);
  background: var(--primary-brand);
  color: var(--text-white);
  border-radius: var(--border-radius-radius-sm);
  border: 1px solid var(--primary-brand);
  min-width: 300px;
`;

const ToastMessage = styled.span`
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-white);
  cursor: pointer;
  font-size: var(--type-size-sm-5);
  padding: var(--spacing-xs-4) var(--spacing-xs-8);
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`;

/**
 * Custom hook that shows a toast notification when analysis completes
 * but no claims needing citation were found.
 * Only shows once per component mount.
 *
 * @param options - Configuration options
 * @param options.withButton - If true, shows a "New Analysis" button (for Simple UI)
 * @param options.isDocumentLoaded - If provided, waits for document to load before showing toast (for Inline UI)
 */
export const useNoClaimsToast = (options?: {
  withButton?: boolean;
  isDocumentLoaded?: boolean;
}): void => {
  const withButton = options?.withButton ?? false;
  const isDocumentLoaded = options?.isDocumentLoaded ?? true;
  const context = useContext(CiterightContext);
  const { toast } = useToast();
  const hasShownToast = useRef(false);

  useEffect(() => {
    const report = context?.report;
    if (!report) return;

    // Show toast when classification is complete with no claims found
    // and document is loaded (if applicable)
    if (
      report.classify_text_complete &&
      report.claims?.length === 0 &&
      !hasShownToast.current &&
      isDocumentLoaded
    ) {
      hasShownToast.current = true;

      if (withButton) {
        // Simple UI: Toast with "New Analysis" button
        const handleNewAnalysis = () => {
          const params = new URLSearchParams(window.location.search);
          params.delete('report_id');
          params.delete('claim_id');
          const newUrl = params.toString()
            ? `${window.location.pathname}?${params.toString()}`
            : `${window.location.pathname}?ui=simple`;
          window.open(newUrl, '_blank', 'noopener,noreferrer');
        };

        const NoClaimsToast = (_id: string, closeToast?: () => void) => (
          <ToastContent>
            <ToastMessage>No claims needing citation found.</ToastMessage>
            <Button
              size="sm"
              inverse
              onClick={() => {
                handleNewAnalysis();
                closeToast?.();
              }}
              iconRight="open_in_new"
            >
              New Analysis
            </Button>
            <CloseButton onClick={closeToast} aria-label="Close">
              ×
            </CloseButton>
          </ToastContent>
        );

        toast({
          text: 'No claims needing citation found.',
          render: NoClaimsToast,
          placement: 'bottom-end',
          autoClose: false,
          closeable: true,
        });
      } else {
        // Normal/Inline UI: Simple text toast
        toast({
          text: 'No claims needing citation found in your text.',
          placement: 'bottom-end',
          autoClose: true,
          delay: 10000,
          closeable: true,
        });
      }
    }
  }, [context?.report, toast, withButton, isDocumentLoaded]);
};
